// Transform API response into lookup functions for FiltersSidebar
// API returns { domains: [{ name, groups: [{ name, units: [{ unit }] }] }] }

/**
 * Build a hierarchy lookup from API response.
 * @param {Object} apiResponse - { domains: [...] } from /filter-options endpoint
 * @returns {Object} - { domainsByName, groupsByDomain, unitsByGroup }
 */
export function buildHierarchy(apiResponse) {
  const domainsByName = {};
  const groupsByDomain = {};
  const unitsByGroup = {};

  if (!apiResponse?.domains) return { domainsByName, groupsByDomain, unitsByGroup };

  apiResponse.domains.forEach(domain => {
    domainsByName[domain.name] = true;
    groupsByDomain[domain.name] = [];

    domain.groups?.forEach(group => {
      groupsByDomain[domain.name].push(group.name);
      unitsByGroup[group.name] = [];

      group.units?.forEach(unit => {
        unitsByGroup[group.name].push(unit.unit);
      });
    });
  });

  return { domainsByName, groupsByDomain, unitsByGroup };
}

/**
 * Get all available domain names from hierarchy.
 */
export function getAllDomains(hierarchy) {
  return Object.keys(hierarchy.domainsByName).sort();
}

/**
 * Get groups available for selected domains (all groups if none selected).
 */
export function groupsFor(selectedDomains, hierarchy) {
  if (!selectedDomains || selectedDomains.length === 0) {
    return Object.values(hierarchy.groupsByDomain).flat();
  }
  const groups = [];
  selectedDomains.forEach(domain => {
    (hierarchy.groupsByDomain[domain] || []).forEach(group => {
      if (!groups.includes(group)) groups.push(group);
    });
  });
  return groups.sort();
}

/**
 * Get units available for selected groups (all units if none selected).
 */
export function unitsFor(selectedGroups, hierarchy) {
  if (!selectedGroups || selectedGroups.length === 0) {
    return Object.values(hierarchy.unitsByGroup).flat();
  }
  const units = [];
  selectedGroups.forEach(group => {
    (hierarchy.unitsByGroup[group] || []).forEach(unit => {
      if (!units.includes(unit)) units.push(unit);
    });
  });
  return units.sort();
}

// Keep legacy exports for backward compatibility
export const DOMAIN_HIERARCHY = [
  {
    domain: 'Analytics Intelligence',
    groups: [
      { group: 'Content & Experience Analysis', units: ['Content Analysis', 'Error Analysis', 'Page Performance Analysis'] },
      { group: 'Performance & Lifecycle Analytics', units: ['Channel Performance', 'Users & Retention', 'AI Analyst'] },
      { group: 'Mobile Analytics', units: ['SDK Session Replay & Errors', 'SDK Native Core'] },
    ],
  },
  {
    domain: 'Core Tech',
    groups: [
      { group: 'Platform', units: ['Core Cloud', 'Data Storage', 'Data Ingest'] },
      { group: 'Data Infrastructure', units: ['Data Connect', 'PA Query Engine'] },
      { group: 'Tag', units: ['Tag Core', 'Tag Functions'] },
    ],
  },
  {
    domain: 'Conversation Intelligence',
    groups: [
      { group: 'Conversation Intelligence', units: ['VoC', 'Customer Journey Analysis'] },
    ],
  },
  {
    domain: 'EPD Office',
    groups: [
      { group: 'Program, Ops & Quality Excellence', units: ['Quality Excellence'] },
    ],
  },
];

export const ALL_DOMAINS = DOMAIN_HIERARCHY.map(d => d.domain);
export const ALL_GROUPS = DOMAIN_HIERARCHY.flatMap(d => d.groups.map(g => g.group));
export const ALL_UNITS = DOMAIN_HIERARCHY.flatMap(d => d.groups.flatMap(g => g.units));
